<template>
  <div
    class="fts-table-edit-container"
    :class="{'fts-scrollbar': overflowAuto}"
    ref="ftsTableEdit"
    tabindex="0"
  >
    <table
      class="fts-table-edit"
      :class="[
        comfortDesign ? 'comfort-design' : 'compact-design',
        { 'table-edit--sticky-header': stickyHeader }
      ]"
      @keydown.tab="evt => shortcutTab(evt)"
    >
      <thead ref="thead">
        <tr>
          <th class="table-row-action default-width" v-if="isTableControl"></th>
          <th
            v-for="colItem in fields"
            :key="colItem.id"
            :style="colItem.style"
          >
            <div class="col-label">
              <div class="col-label-name">{{colItem.label || colItem.displayName}}</div>
              <i 
                class="col-label-icon" 
                :class="[
                  colItem.informationIcon, 
                  {'required-dot': colItem.required && !colItem.readOnly}
                ]" 
                v-if="hideLabelIcon !== true"
              ></i>
            </div>
          </th>
          <th class="th-design-wrapper" v-if="designMode">
            <fts-form-design-shortcut
              :selected-options="item && item.fields"
              :exclude-types="designExcludeTypes"
              suggestion
              @option:selected="evt => $emit('design:column', evt)"
              @option:create="evt => $emit('design:column', evt)"
            >
              <fts-tippy trigger-class="th-design-toggle">
                <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
                <div slot="content" class="tippy-hint">
                  <span class="tippy-hint-highlight">Click</span> to add a field
                </div>
              </fts-tippy>
            </fts-form-design-shortcut>
          </th>
        </tr>
      </thead>

      <draggable
        v-model="formData[item.fieldName]"
        v-bind="dragOptions"
        handle=".row-action-handle"
        @start="drag = true"
        @end="drag = false"
        tag="tbody"
        ref="table-tbody"
      >
        <tr
          v-for="(colData, colDataInd) in formData[item.fieldName]"
          :key="colData.id"
          :class="{'selected-row': selectedRow && selectedRow.id === colData.id }"
          @click.capture="selectRow(colData)"
        >
          <!-- actions -->
          <td class="table-row-action default-width" v-if="isTableControl">
            <div>
              <font-awesome-icon
                :icon="['fas', 'grip-vertical']"
                fixed-width
                class="row-action-handle"
                v-if="!selectedRow" />

              <font-awesome-icon
                :icon="['fas', 'plus']"
                fixed-width
                class="row-action-add"
                v-else
                @click.stop="addTableRow(colDataInd + 1)" />

              <font-awesome-icon
                :icon="['fas', 'trash']"
                fixed-width
                class="row-action-remove"
                @click.stop="removeTableRow(colData)" />
            </div>
          </td>
          <!-- fields -->
          <td
            v-for="(colItem, colItemIndex) in fields"
            :key="colItem.id"
            :style="colItem.style"
            :class="{'col-uploading': isColumnUploading(colData.id, colItem)}"
            :ref="`td${colData.id}${colItem.fieldName}`"
            @click="smartFocus(colItem, colData.id, colDataInd)"
            @keydown.ctrl.v.exact.self="evt => shortcutPaste(evt)"
            @keydown.exact.self="evt => shortcutKeydown(evt)"
            @keydown.shift.exact.self="evt => shortcutKeydown(evt)"
            @keydown.enter.exact.self.prevent="evt => shortcutEnter(evt)"
            @keydown.shift.enter.exact.capture="evt => shortcutAddRow(evt)"
            tabindex="0"
            :data-row-index="colDataInd"
            :data-column-index="colItemIndex"
          >
            <!-- upload overlay -->
            <div class="upload-overlay" v-if="isColumnUploading(colData.id, colItem)">
              uploading
            </div>
            <!-- form display -->
            <fts-display
              class="form-display-borderless"
              :class="colItem.elementDisplayClass"
              :item="colItem"
              :form-data="colData"
              :vector-id="vectorId"
              :view="view"
              :share-form="shareForm"
              in-table
              :ref="`formDisplayBorderless${colItem.id}${colData.id}`"
            />
            <!-- form edit -->
            <div
              class="form-element-edit form-element-borderless"
              :ref="`formElementBorderless${colItem.id}${colData.id}`"
              @click.stop.self="smartFocus(colItem, colData.id, colDataInd)"
              @keydown.enter="evt => shortcutFieldEnter(evt)"
              @keydown.escape="evt => shortcutFieldEscape(evt)"
              v-if="selectedRow && selectedRow.id === colData.id || isColumnUploading(colData.id, colItem)"
            >
              <!-- display -->
              <fts-display
                class="form-display-borderless"
                :item="colItem"
                :form-data="colData"
                :vector-id="vectorId"
                :view="view"
                :share-form="shareForm"
                in-table
                :comfort-design="comfortDesign"
                :class="colItem.elementDisplayClass"
                v-if="
                  colItem.display ||
                  colItem.readOnly ||
                  colItem.informationInput === 'display' ||
                  (
                    (
                      colItem.type == 33 ||
                      colItem.informationInput === 'link'
                    ) && isCustomLink(colData, colItem.fieldName)
                  )
                "
              />
              <!-- custom formatted text -->
              <cleave
                class="fts-form-control"
                v-model="colData[colItem.fieldName]"
                :options="colItem.options"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="colItem.informationInput == 'formattedText'"
              />
              <!-- module ,select -->
              <fts-dropdown
                :item="colItem"
                :form-data="colData"
                :comfort-design="comfortDesign"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName);columnOnBlurFocusTd()"
                v-else-if="(
                  colItem.type == 7
                  || colItem.type == 27
                  || colItem.informationInput == 'dropDown'
                  || colItem.informationInput == 'select'
                ) && colItem.type != 20"
              />
              <!-- locuses, tag -->
              <fts-dropdown
                :item="colItem"
                :form-data="colData"
                multiple
                observer-height
                :comfort-design="comfortDesign"
                @updateHeight="setDisplayHeight($event, colItem.id, colData.id, colDataInd)"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName);columnOnBlurFocusTd()"
                v-else-if="
                  colItem.type == 8 ||
                  colItem.type == 28
                "
              />
              <!-- location -->
              <fts-location
                :item="colItem"
                :form-data="colData"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @close="columnValidatorTouch(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 9 ||
                  colItem.informationInput == 'location'
                "
              />
              <!-- multiline text -->
              <fts-text-area
                :item="colItem"
                :form-data="colData"
                :rows="1"
                @updateHeight="setDisplayHeight($event, colItem.id, colData.id, colDataInd)"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 16 ||
                  colItem.informationInput == 'textArea'
                "
              />
              <!-- date time -->
              <fts-date-time
                :min-datetime="colItem.minDatetime"
                :max-datetime="colItem.maxDatetime"
                :item="colItem"
                :form-data="colData"
                full-width
                :comfort-design="comfortDesign"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @close="columnValidatorTouch(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 5 ||
                  colItem.type == 6 ||
                  colItem.informationInput == 'dateTime' ||
                  colItem.informationInput == 'date' ||
                  colItem.informationInput == 'time'
                "
              />
              <!-- integer -->
              <fts-integer
                :item="colItem"
                :form-data="colData"
                controls
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 2 ||
                  colItem.informationInput == 'integer'
                "
              />
              <!-- file upload -->
              <fts-file-upload
                :item="colItem"
                :form-data="colData"
                :vector-id="vectorId"
                :comfort-design="comfortDesign"
                @click.stop
                @onQueue="setFileQueue($event, colData.id, colItem.fieldName)"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 10 ||
                  colItem.type == 11 ||
                  colItem.type == 12 ||
                  colItem.type == 13 ||
                  colItem.informationInput == 'imageUpload' ||
                  colItem.informationInput == 'imagesUpload' ||
                  colItem.informationInput == 'fileUpload' ||
                  colItem.informationInput == 'filesUpload'
                "
              />
              <!-- content -->
              <fts-content
                :item="colItem"
                :form-data="colData"
                :data="colData[colItem.fieldName]"
                float-menu-bar
                @updateContentHeight="setDisplayHeight($event, colItem.id, colData.id, colDataInd)"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 22 ||
                  colItem.informationInput == 'content'
                "
              />
              <!-- number / money -->
              <fts-number-currency
                class="fts-form-control number-currency"
                :item="colItem"
                :form-data="colData"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 3 ||
                  colItem.type == 4 ||
                  colItem.informationInput == 'number' ||
                  colItem.informationInput == 'currency'
                "
              />
              <!-- yes no -->
              <checkbox
                v-model="colData[colItem.fieldName]"
                :color="'#279ec6'"
                :size="20"
                @change="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 1 ||
                  colItem.informationInput == 'boolean' ||
                  colItem.informationInput == 'toggle'
                "
              />
              <!-- profile (module) -->
              <fts-user
                :item="colItem"
                :form-data="colData"
                :comfort-design="comfortDesign"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName);columnOnBlurFocusTd()"
                v-else-if="
                  colItem.type == 20 ||
                  colItem.informationInput == 'dropDownUser'
                "
              />
              <!-- users -->
              <fts-user
                :item="colItem"
                :form-data="colData"
                multiple
                observer-height
                :comfort-design="comfortDesign"
                @updateHeight="setDisplayHeight($event, colItem.id, colData.id, colDataInd)"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName);columnOnBlurFocusTd()"
                v-else-if="colItem.type == 21"
              />
              <!-- phone number -->
              <fts-phone-number
                :item="colItem"
                :form-data="colData"
                :comfort-design="comfortDesign"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 26 ||
                  colItem.informationInput == 'phone'
                "
              />
              <!-- signature -->
              <fts-signature
                :vector-id="vectorId"
                :form-data="colData"
                :item="colItem"
                :modal-mode="shareForm"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                v-else-if="
                  colItem.type == 29 ||
                  colItem.informationInput == 'signature'
                "
              />
              <!-- default -->
              <input
                class="fts-form-control"
                type="text"
                autocomplete="off"
                v-model="colData[colItem.fieldName]"
                @input="columnOnInput(colData.id, colItem.fieldName)"
                @blur="columnOnBlur(colData.id, colItem.fieldName)"
                @paste="event => sanitizePastedText(event, colData, colItem.fieldName)"
                v-else
              />
            </div>
          </td>
          <td v-if="designMode"></td>
        </tr>
      </draggable>
      <tr
        class="table-add-row"
        @click="addTableRow(formData[item.fieldName]? formData[item.fieldName].length : 0)"
        v-if="isTableControl"
      >
        <td :colspan="designMode ? fields.length + 2 : fields.length + 1">
          <div>
            <font-awesome-icon :icon="['fas', 'plus']" fixed-width />add new
          </div>
        </td>
      </tr>
    </table>

    <div
      class="fts-table-form-action"
      :class="{'comfort-design': comfortDesign}"
      v-if="isTableControl"
    >
      <span v-if="selectedRow">
        Press&nbsp;<kbd>ESC</kbd>&nbsp;to unselect.
      </span>
      <span v-if="!selectedRow">
        <font-awesome-icon :icon="['fas', 'grip-vertical']" fixed-width />Click and drag to row to sort.
      </span>
      <span v-if="selectedRow">
        <font-awesome-icon :icon="['fas', 'trash']" fixed-width />Click and remove it.
      </span>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import Checkbox from 'vue-material-checkbox';

import commons from '../commons/commons';
import { Information } from '../commons/flags';
import { authorizationStore } from '../store/authorizationStore';

import FTSContent from './FTSContent';
import FTSDateTime from './FTSDateTime';
import FTSDisplay from './FTSDisplay';
import FTSDropdown from './FTSDropdown';
import FTSFileUpload from './FTSFileUpload';
import FTSFormDesignShortcut from '../design/FTSFormDesignShortcut';
import FTSInteger from './FTSInteger';
import FTSLocation from './FTSLocation';
import FTSNumberCurrency from './FTSNumberCurrency';
import FTSPhoneNumber from './FTSPhoneNumber';
import FTSSignature from './FTSSignature';
import FTSTextArea from './FTSTextArea';
import FTSTippy from './FTSTippy';
import FTSUser from './FTSUser';

const setElementSelectionRange = (el) => {
  if (el.type !== 'email' && el.type !== 'number') {
    el.setSelectionRange(el.value.length, el.value.length);
  }
}
const getElementByTagName = (event, tagName) => Array.prototype.find.call(event.path || event.composedPath(), (x) => x.tagName === tagName);
const getTabbableTdElementsFromParent = (parent) => Array.from(parent.querySelectorAll("td[tabindex]"));
const getTabbableTdAttribute = (el) => ({
  rowIndex: parseInt(el.getAttribute('data-row-index')),
  columnIndex: parseInt(el.getAttribute('data-column-index'))
});
const findScrollableParent = (el) => {
  while (el && el !== document.body) {
    const overflowY = getComputedStyle(el).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }

  return document.scrollingElement || document.documentElement;
}

export default {
  components: {
    draggable,
    checkbox: Checkbox,
    'fts-content': FTSContent,
    'fts-date-time': FTSDateTime,
    'fts-display': FTSDisplay,
    'fts-dropdown': FTSDropdown,
    'fts-file-upload': FTSFileUpload,
    'fts-form-design-shortcut': FTSFormDesignShortcut,
    'fts-integer': FTSInteger,
    'fts-location': FTSLocation,
    'fts-number-currency': FTSNumberCurrency,
    'fts-phone-number': FTSPhoneNumber,
    'fts-signature': FTSSignature,
    'fts-text-area': FTSTextArea,
    'fts-tippy': FTSTippy,
    'fts-user': FTSUser
  },
  props: {
    item: Object,
    formData: Object,
    vectorId: [Number, String],
    validator: Object,
    shareForm: Boolean,
    comfortDesign: Boolean,
    overflowAuto: Boolean,
    view: Boolean,
    designMode: Boolean,
    stickyHeader: Boolean,
    offset: {
      type: Object,
      default: () => ({
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      })
    },
    hideLabelIcon: Boolean
  },
  data: () => ({
    dragOptions: commons.dragOptions,
    selectedRow: null,
    fileQueues: [],
    scrollContainer: null
  }),
  methods: {
    addTableRow(index) {
      let newRow = { id: commons.newUUID()};

      this.item.fields.forEach(x => {
        if (x && x.fieldName.indexOf(".") === -1) {
          this.$set(newRow, x.fieldName, null);
        }
      });

      if (this.formData[this.item.fieldName] == null) {
        this.$set(this.formData, this.item.fieldName, []);
      }

      this.formData[this.item.fieldName].splice(index, 0, newRow);
      this.selectRow(newRow);
    },
    removeTableRow(item) {
      this.selectRow(null);
      let tableData = this.formData[this.item.fieldName];
      tableData.splice(tableData.indexOf(item), 1);
    },
    setDisplayHeight(elHeight, itemId, colDataId, colDataInd) {
      let el = this.$refs[`formDisplayBorderless${itemId}${colDataId}`];
      let itemIndexOf = 0;
      
      if (el && el.length > 1)
        itemIndexOf = colDataInd;

      el[itemIndexOf].$el.style.minHeight = elHeight;
    },
    smartFocus(colItem, colDataId, colDataInd) {
      if (colItem.readOnly || commons.elementStopPropagation()) return;

      const element = this.$refs[`formElementBorderless${colItem.id}${colDataId}`];
      let itemIndexOf = 0;
      const setSelectionRange = (el) => {
        try {
          el.focus();
          if (el.type !== 'email' && el.type !== 'number') {
            el.setSelectionRange(el.value.length, el.value.length);
          }
        } catch(err) {
          console.warn("[DOMException]", err);
        }
      }
      if (element && element.length > 1) {
        itemIndexOf = colDataInd;
      }

      const tdEl = element[itemIndexOf].parentElement;

      if (tdEl && tdEl.tagName == "TD") {
        const tdIndex = tdEl.getAttribute("data-column-index");
        this.columnScrollIntoView(tdEl, tdIndex);
      }

      try {
        if (
          [0, 3, 4, 25, 33].indexOf(colItem.type) > -1 ||
          ["text", "number", "email", "link"].indexOf(colItem.informationInput) > -1
        ) {
          setSelectionRange(element[itemIndexOf].firstChild);
        } else if (
          [2, 5, 6, 7, 8, 20, 21, 27, 28].indexOf(colItem.type) > -1 ||
          ["integer", "date", "dateTime", "time", "dropDown", "dropDownUser", "select"].indexOf(colItem.informationInput) > -1
        ) {
          element[itemIndexOf].firstChild.getElementsByTagName("input")[0].focus();
        } else if (colItem.type === 16 || colItem.informationInput === "textArea") {
          setSelectionRange(element[itemIndexOf].getElementsByTagName("textarea")[0]);
        } else if (colItem.type === 22 || colItem.informationInput === "content") {
          element[itemIndexOf].firstChild.__vue__.editor.focus('end');
        } else if (colItem.type === 26 || colItem.informationInput === "phoneNumber") {
          setSelectionRange(element[itemIndexOf].getElementsByTagName("input")[1]);
        } else if (colItem.type === 9  || colItem.informationInput === "location") {
          element[itemIndexOf].getElementsByClassName("location-form-control")[0].focus();
        } else if (this.shareForm && (colItem.type == 29 || colItem.informationInput == 'signature')) {
          element[itemIndexOf].firstChild.__vue__.modalShow();
        }
      } catch(err) {
        console.warn("[DOMException]", err);
      }
    },
    selectRow(item) {
      if (commons.elementStopPropagation()) return;

      this.selectedRow = item;
    },
    setFileQueue(queue, colId, fieldName) {
      const uuid = `ftsFileUpload-${colId}-${fieldName}`;
      let findItem = this.fileQueues.find(x => x.id === uuid);
      if (findItem) {
        if (queue) {
          this.$set(findItem, 'queue', queue);
        } else {
          const findIndex = this.fileQueues.findIndex(x => x.id === uuid);
          if (findIndex > -1)
            this.fileQueues.splice(findIndex, 1);
        }
      } else if (queue) {
        this.fileQueues.push({ id: uuid, queue: queue});
      }
    },
    isColumnDisplay(column, columnData) {
      return (
        column.display ||
        column.readOnly ||
        column.informationInput === 'display' ||
        (
          (
            column.type == 33 ||
            column.informationInput === 'link'
          ) && isCustomLink(columnData, column.fieldName)
        )
      )
    },
    isColumnUploading(colDataId, colItem) {
      if (
        (
          colItem.type == 10 ||
          colItem.type == 11 ||
          colItem.type == 12 ||
          colItem.type == 13 ||
          colItem.informationInput == 'imageUpload' ||
          colItem.informationInput == 'imagesUpload' ||
          colItem.informationInput == 'fileUpload' ||
          colItem.informationInput == 'filesUpload'
        ) &&
        this.fileQueues &&
        this.fileQueues.length &&
        (
          this.selectedRow === null ||
          (this.selectedRow && this.selectedRow.id !== colDataId)
        )
      ) {
        return this.fileQueues.some(x => x.id === `ftsFileUpload-${colDataId}-${colItem.fieldName}`);
      }
    },
    isCustomLink(colData, fieldName) {
      return Object.prototype.toString.call(colData[fieldName]) === '[object Object]';
    },
    escapeClick(e) {
      try {
        const el = this.$el.getElementsByTagName("table")[0] || this.$el;
        if (!el.contains(e.target) && this.selectedRow) {
          this.selectRow(null);
        }
      } catch(err) {
        console.warn("TableEdit:EscapeClick", err);
      }
    },
    escapeHandler(e) {
			if (e.keyCode === 27) {
        e.stopPropagation()
        this.selectRow(null);
      }
    },
    /* validator */
    columnValidatorTouch(rowId, colFieldName) {
      try {
        const validator = Object.values(this.validator.$each.$iter).find(x => x.$model.id == rowId);
        let column = validator && validator[colFieldName];
        if (column) {
          column.$touch();
          let validator = this.$refs[`td${rowId}${colFieldName}`];
          if (validator && validator[0]) {
            if (column.$invalid && column.$dirty) {
              validator[0].classList.add('col-error');
            } else {
              validator[0].classList.remove('col-error');
            }
          }
        }
      } catch(err) {
        console.error(err);
      }
    },
    columnOnInput: _.debounce(function (rowId, colFieldName) {
      this.columnValidatorTouch(rowId, colFieldName);
    }, 500),
    columnOnBlur(rowId, colFieldName) {
      this.$nextTick(() => {
        this.columnValidatorTouch(rowId, colFieldName);
      });
    },
    columnOnBlurFocusTd() {
      const event = window.event;
      if (event.relatedTarget === null) {
        const tdEl = getElementByTagName(event, 'TD');
        tdEl && tdEl.focus({ preventScroll: true });
      }
    },
    columnScrollIntoView(colEl, colElIndex) {
      if (!this.scrollContainer) {
        this.scrollContainer = findScrollableParent(this.$el);
      }

      if (!colEl || !this.scrollContainer) {
        return;
      }

      const theadHeight = this.$refs.thead.getBoundingClientRect().height;
      const additionalOffset = this.offset || {};
      const offset = {
        top: theadHeight + (additionalOffset.top || 0),
        bottom: 16 + (additionalOffset.bottom || 0),
        left: 6 + (additionalOffset.left || 0),
        right: 16 + (additionalOffset.right || 0)
      };

      const cellRect = colEl.getBoundingClientRect();
      const containerRect = this.scrollContainer.getBoundingClientRect();

      const cellHeight = cellRect.height;
      const cellWidth = cellRect.width;

      const visibleHeight = containerRect.height - offset.top - offset.bottom;
      const visibleWidth = containerRect.width - offset.left - offset.right;

      const visibleTop = containerRect.top + offset.top;
      const visibleBottom = containerRect.bottom - offset.bottom;
      const visibleLeft = containerRect.left + offset.left;
      const visibleRight = containerRect.right - offset.right;

      const isAbove = cellRect.top < visibleTop;
      const isBelow = cellRect.bottom > visibleBottom;
      const isLeft = cellRect.left < visibleLeft;
      const isRight = cellRect.right > visibleRight;

      const allowVerticalScroll = cellHeight < visibleHeight;
      const allowHorizontalScroll = cellWidth < visibleWidth;

      let newScrollTop = this.scrollContainer.scrollTop;
      let newScrollLeft = this.scrollContainer.scrollLeft;

      if (allowVerticalScroll) {
        if (isAbove) {
          newScrollTop += cellRect.top - visibleTop;
        } else if (isBelow) {
          newScrollTop += cellRect.bottom - visibleBottom;
        }
      } else {
        const findEl = Array.from(colEl.children).find(el => el.classList.contains("form-element-borderless"));

        if (findEl && findEl.firstChild) {
          const targetElRect = findEl.firstChild.getBoundingClientRect();
          const targetElHeight = targetElRect.height;

          if (targetElHeight < visibleHeight) {
            newScrollTop += targetElRect.top - visibleTop;
          }
        }
      }

      if (allowHorizontalScroll) {
        if (colElIndex == 0) {
          newScrollLeft = 0;
        } else if (isLeft) {
          newScrollLeft += cellRect.left - visibleLeft;
        } else if (isRight) {
          newScrollLeft += cellRect.right - visibleRight;
        }
      }

      if (newScrollTop !== this.scrollContainer.scrollTop || newScrollLeft !== this.scrollContainer.scrollLeft) {
        this.scrollContainer.scrollTo({
          top: newScrollTop,
          left: newScrollLeft
        });
      }
    },
    sanitizePastedText(event, colData, colFieldName) {
      commons.sanitizePastedText(event, (value) => {
        this.$set(colData, colFieldName, value);
      });
    },
    getInvalidColumns: _.debounce(function() {
      try {
        const list = Object.values(this.validator.$each.$iter)
          .filter(x => x.$invalid)
          .reduce((accumulator, item) => {
            const columns = Object.keys(item)
              .filter(x => item[x].$invalid)
              .map(y => ({ rowId: item.$model.id, colFieldName: y}))

            if (columns && columns.length) {
              return accumulator.concat(columns);
            }
          }, []);

        if (list && list.length) {
          list.forEach(x => {
            let el = this.$refs[`td${x.rowId}${x.colFieldName}`];
            el && el[0] && el[0].classList.add('col-error');
          });
        }
      } catch(err) {
        console.error(err);
      }
    }, 500),
    getFieldElementFromParentByType(parent, fieldType) {
      switch(fieldType) {
        case Information.Text.ID:
        case Information.Integer.ID:
        case Information.Number.ID:
        case Information.Money.ID:
        case Information.Date.ID:
        case Information.DateTime.ID:
        case Information.Email.ID:
        case Information.Phone.ID:
        case Information.URL.ID:
          return parent.querySelector("input.fts-form-control");
        case Information.YesNo.ID:
          return parent.querySelector(".m-chckbox--container");
        case Information.Location.ID:
          return parent.querySelector("input.location-form-control");
        case Information.Module.ID:
        case Information.Modules.ID:
        case Information.User.ID:
        case Information.Users.ID:
        case Information.Select.ID:
        case Information.Tag.ID:
          return parent.querySelector("input.vs-input");
        case Information.MultilineText.ID:
          return parent.querySelector("textarea");
        case Information.Content.ID:
          return parent.querySelector(".fts-editor");
      }
    },
    switchRowColumnByIndex(rowIndex, columnIndex) {
      const elements = getTabbableTdElementsFromParent(this.$refs['table-tbody'].$el.children[rowIndex]);
      const colEl = elements[columnIndex];
      this.columnScrollIntoView(colEl, columnIndex);
      colEl.focus();
    },
    shortcutPaste(event) {
      const { columnIndex } = getTabbableTdAttribute(event.target);
      const field = this.fields[columnIndex];
      if (field) {
        const fieldEl = this.getFieldElementFromParentByType(event.target, field.type);
        if ([
          Information.Text.ID,
          Information.Integer.ID,
          Information.Number.ID,
          Information.Money.ID,
          Information.Date.ID,
          Information.DateTime.ID,
          Information.Location.ID,
          Information.MultilineText.ID,
          Information.Email.ID,
          Information.Phone.ID,
          Information.URL.ID,
          Information.Module.ID, 
          Information.Modules.ID, 
          Information.User.ID, 
          Information.Users.ID, 
          Information.Select.ID, 
          Information.Tag.ID
        ].indexOf(field.type) > -1) {
          fieldEl.focus();
          fieldEl.select();
          fieldEl.dispatchEvent(new Event("paste"));
        }
      }
    },
    shortcutKeydown(event) {
      if (event.key.length == 1) {
        const { columnIndex } = getTabbableTdAttribute(event.target);
        const field = this.fields[columnIndex];
        if (field) {
          const fieldEl = this.getFieldElementFromParentByType(event.target, field.type);
          if ([
            Information.Text.ID,
            Information.Integer.ID,
            Information.Number.ID,
            Information.Money.ID,
            Information.Date.ID,
            Information.DateTime.ID,
            Information.Location.ID,
            Information.MultilineText.ID,
            Information.Email.ID,
            Information.Phone.ID,
            Information.URL.ID
          ].indexOf(field.type) > -1) {
            fieldEl.focus();
            fieldEl.select();
            fieldEl.dispatchEvent(new Event("input"));
          } else if ([
            Information.Module.ID, 
            Information.Modules.ID, 
            Information.User.ID, 
            Information.Users.ID, 
            Information.Select.ID, 
            Information.Tag.ID
          ].indexOf(field.type) > -1) {
            fieldEl.focus();
            fieldEl.dispatchEvent(new Event("input"));
          } else if (field.type == Information.Content.ID) {
            fieldEl.__vue__.editor.focus('end');
          }
        }
      } else if (event.keyCode == 9 || (event.keyCode >= 37 && event.keyCode <= 40)) {
        event.preventDefault();
        const trEl = getElementByTagName(event, "TR");
        const tabbableTdElements = getTabbableTdElementsFromParent(trEl);
        let findIndex = tabbableTdElements.indexOf(event.target);
        const { rowIndex, columnIndex } = getTabbableTdAttribute(event.target);
        if (findIndex > -1) {
          const list = this.formData[this.item.fieldName];
          if (event.keyCode == 9) { // tab
            if (event.shiftKey) {
              if (findIndex > 0) {
                const colEl = tabbableTdElements[findIndex - 1];
                this.columnScrollIntoView(colEl, findIndex - 1);
              }
            } else {
              if (tabbableTdElements[findIndex + 1]) {
                const colEl = tabbableTdElements[findIndex + 1];
                this.columnScrollIntoView(colEl, findIndex + 1);
              }
            }
          } else if (event.keyCode == 37) { // left
            if (findIndex > 0) {
              const colEl = tabbableTdElements[findIndex - 1];
              this.columnScrollIntoView(colEl, findIndex - 1);
              colEl.focus();
            } else if (rowIndex > 0) {
              this.selectRow(list[rowIndex - 1]);
              this.$nextTick(() => {
                this.switchRowColumnByIndex(rowIndex - 1, this.fields.length - 1);
              }); 
            }
          } else if (event.keyCode == 39) { // right
            if (tabbableTdElements[findIndex + 1]) {
              const colEl = tabbableTdElements[findIndex + 1];
              this.columnScrollIntoView(colEl, findIndex + 1);
              colEl.focus();
            } else if (list.length - 1 > rowIndex) {
              this.selectRow(list[rowIndex + 1]);
              this.$nextTick(() => {
                this.switchRowColumnByIndex(rowIndex + 1, 0);
              });
            }
          } else if (event.keyCode == 38) { // up
            if (rowIndex > 0) {
              this.selectRow(list[rowIndex - 1]);
              this.$nextTick(() => {
                this.switchRowColumnByIndex(rowIndex - 1, columnIndex);
              });
            }
          } else if (event.keyCode == 40) { // down
            if (list.length - 1 > rowIndex) {
              this.selectRow(list[rowIndex + 1]);
              this.$nextTick(() => {
                this.switchRowColumnByIndex(rowIndex + 1, columnIndex);
              });
            }
          }
        }
      }
    },
    shortcutTab(event) {
      const trEl = getElementByTagName(event, "TR");
      const tabbableTdElements = getTabbableTdElementsFromParent(trEl);
      let findIndex = tabbableTdElements.indexOf(event.target);
      let rowIndex = -1;
      let columnIndex = -1;
      if (findIndex > -1) {
        const attribute = getTabbableTdAttribute(event.target);
        rowIndex = attribute.rowIndex;
        columnIndex = attribute.columnIndex
      } else {
        const tdEl = getElementByTagName(event, "TD");
        if (tdEl) {
          findIndex = tabbableTdElements.indexOf(tdEl);
          const attribute = getTabbableTdAttribute(tdEl);
          rowIndex = attribute.rowIndex;
          columnIndex = attribute.columnIndex;
        }
      }
      if (findIndex > -1 && commons.isInteger(rowIndex) && commons.isInteger(columnIndex)) {
        const list = this.formData[this.item.fieldName];
        const trTotalCount = list.length;
        if (event.shiftKey) {
          if (tabbableTdElements[findIndex - 1]) {
            tabbableTdElements[findIndex - 1].focus();
            event.preventDefault();
          } else if (rowIndex > 0) {
            this.selectRow(list[rowIndex - 1]);
            this.$nextTick(() => {
              this.switchRowColumnByIndex(rowIndex - 1, this.fields.length - 1);
            });
            event.preventDefault();
          }
        } else {
          if (tabbableTdElements[findIndex + 1]) {
            tabbableTdElements[findIndex + 1].focus();
            event.preventDefault();
          } else if (trTotalCount - 1 > rowIndex) {
            this.selectRow(list[rowIndex + 1]);
            this.$nextTick(() => {
              this.switchRowColumnByIndex(rowIndex + 1, 0);
            });
            event.preventDefault();
          }
        }
      }
    },
    shortcutAddRow(event) {
      if (this.isTableControl && event && event.target && event.target.tagName == 'TD') {        
        const { rowIndex, columnIndex } = getTabbableTdAttribute(event.target);
        if (commons.isInteger(rowIndex) && commons.isInteger(columnIndex)) {
          event.preventDefault();
          this.addTableRow(rowIndex + 1);
          this.$nextTick(() => {
            this.switchRowColumnByIndex(rowIndex + 1, columnIndex);
          });
        }
      }
    },
    shortcutEnter(event) {
      const dataColumnIndex = event.target.getAttribute('data-column-index');
      const field = this.fields[dataColumnIndex];
      if (field) {
        const fieldEl = this.getFieldElementFromParentByType(event.target, field.type);
        if (fieldEl) {
          if (field.type == Information.YesNo.ID) {
            fieldEl.__vue__.toggle();
          } else if (
            [
              Information.Module.ID, 
              Information.Modules.ID, 
              Information.User.ID, 
              Information.Users.ID, 
              Information.Select.ID, 
              Information.Tag.ID
            ].indexOf(field.type) > -1
          ) {
            fieldEl.focus();
          } else if ([
            Information.Text.ID,
            Information.Integer.ID,
            Information.Number.ID,
            Information.Money.ID,
            Information.Date.ID,
            Information.DateTime.ID,
            Information.Location.ID,
            Information.MultilineText.ID,
            Information.Email.ID,
            Information.Phone.ID,
            Information.URL.ID,
          ].indexOf(field.type) > -1) {
            fieldEl.focus();
            setElementSelectionRange(fieldEl);
          } else if (field.type == Information.Content.ID) {
            fieldEl.__vue__.editor.focus('end');
          }
        } 
      }
    },
    shortcutFieldEnter(event) {
      const tdEl = getElementByTagName(event, "TD");
      if (tdEl) {
        const { rowIndex, columnIndex } = getTabbableTdAttribute(tdEl);
        const field = this.fields[columnIndex];
        const list = this.formData[this.item.fieldName];
        if (field && !this.isColumnDisplay(field, list[rowIndex]) && list.length - 1 > rowIndex) {
          let nextColumn = false;
          if ([
            Information.Text.ID,
            Information.Integer.ID,
            Information.Number.ID,
            Information.Money.ID,
            Information.Date.ID,
            Information.DateTime.ID,
            Information.Location.ID,
            Information.Email.ID
          ].indexOf(field.type) > -1) {
            nextColumn = true;
          } else if (Information.Phone.ID == field.type) {
            const fieldEl = this.getFieldElementFromParentByType(tdEl, field.type);
            if (fieldEl && fieldEl === event.target) {
              nextColumn = true;
            }
          }
          if (nextColumn) {
            this.selectRow(list[rowIndex + 1]);
            this.switchRowColumnByIndex(rowIndex + 1, columnIndex);
          }
        }
      }
    },
    shortcutFieldEscape(event) {
      const tdEl = getElementByTagName(event, "TD");
      if (tdEl) {
        const { columnIndex } = getTabbableTdAttribute(tdEl);
        const field = this.fields[columnIndex];
        let focusTd = false;
        if (field && [
          Information.Text.ID,
          Information.Integer.ID,
          Information.Number.ID,
          Information.Money.ID,
          Information.Date.ID,
          Information.DateTime.ID,
          Information.MultilineText.ID,
          Information.Email.ID,
          Information.Phone.ID,
          Information.Content.ID
        ].indexOf(field.type) > -1) {
          focusTd = true
        } else if (field.type == Information.Location.ID) {
          const fieldEl = this.getFieldElementFromParentByType(tdEl, field.type);
          if (fieldEl && fieldEl === event.target) {
            focusTd = true;
          }
        }
        if (focusTd) {
          event.stopPropagation();
          tdEl.focus({ preventScroll: true });
        }
      }
    }
  },
  computed: {
    isTableControl() {
      /* hideTableActions (legacy) */
      return !Boolean(this.item && (this.item.hideTableActions || this.item.disabledAddRemoveItems));
    },
    fields() {
      /* item.columns (legacy) */
      if (!this.item.fields && this.item.columns) {
        this.item.fields = this.item.columns;
      }
      let fields = this.item.fields.filter(x => x.input != 'hidden' && !x.hide && x.visible !== false);
      const getColumnDisplayClass = (item) => {
        if (item.input == 'boolean' || item.input == 'toggle' || item.type === 1) {
          return "te-yes-no-display";
        } else if (item.input == 'integer' || item.type === 2) {
          return "te-integer-display";
        } else if (item.input == 'number' || item.input == 'currency' || item.type === 3 || item.type === 4 ) {
          return "te-number-currency-display";
        } else if (!this.shareForm && (item.input == 'signature' || item.type === 29)) {
          return "te-signature-display";
        }
      }

      const getColumnWidth = (defaultValue, customValue) =>
        customValue ?
          customValue > defaultValue ?
            {
              'width': `${customValue}px`,
              'min-width': `${customValue}px`,
              'max-width': `${customValue}px`
            } : {
              'width': `${defaultValue}px`,
              'min-width': `${defaultValue}px`,
              'max-width': `${defaultValue}px`
            }
          : { 'min-width': `${defaultValue}px` }

      const getColumnStyle = (item) => {
        const ColumnWidth = {
          YesNo: 50,
          Content: 395,
          Phone: 200,
          Signature: 300,
          Attachment: 250,
          Display: 35,
          Default: 150
        }

        if (
          item.display ||
          item.input === 'display' ||
          item.readOnly
        ) {
          return getColumnWidth(ColumnWidth.Display, item.columnWidth);
        } else if (
          item.type == 1 ||
          item.input === 'boolean'
        ) {
          return getColumnWidth(ColumnWidth.YesNo, item.columnWidth);
        } else if (
          item.type == 10 ||
          item.type == 11 ||
          item.type == 12 ||
          item.type == 13 ||
          item.input === 'imageUpload' ||
          item.input === 'imagesUpload' ||
          item.input === 'fileUpload' ||
          item.input === 'filesUpload'
        ) {
          return getColumnWidth(ColumnWidth.Attachment, item.columnWidth);
        } else if (
          item.type == 22 ||
          item.input == 'content'
        ) {
          return getColumnWidth(ColumnWidth.Content, item.columnWidth);
        } else if (
          item.type == 26 ||
          item.input === 'phoneNumber'
        ) {
          return getColumnWidth(ColumnWidth.Phone, item.columnWidth);
        } else if (
          item.type == 29 ||
          item.input === 'signature'
        ) {
          return { 'width': `${ColumnWidth.Signature}px` };
        }
        return getColumnWidth(ColumnWidth.Default, item.columnWidth);
      }

      fields.forEach(x => {
        x.elementDisplayClass = getColumnDisplayClass(x);
        x.style = getColumnStyle(x);
        x.informationIcon = commons.informationIcon(x.type);
        if (x.input && !commons.isInteger(x.type)) x.informationInput = x.input;
      });
      return fields;
    },
    designExcludeTypes() {
      let types = [14, 15];
      !authorizationStore.getters.isBeta && (types = types.concat(9));
      return types;
    }
  },
  watch: {
    selectedRow(newVal, oldVal) {
      if (oldVal === null && newVal) {
        document.addEventListener('click', this.escapeClick);
        this.$refs.ftsTableEdit.addEventListener('keydown', this.escapeHandler);
      } else if (oldVal && newVal === null) {
        document.removeEventListener('click', this.escapeClick);
        this.$refs.ftsTableEdit.removeEventListener('keydown', this.escapeHandler);
      }
    },
    validator: {
      deep: true,
      handler(val) {
        if (val.$invalid && val.$dirty) {
          this.getInvalidColumns();
        } else if (!val.$invalid && val.$dirty) {
          let elements = this.$el.getElementsByClassName('col-error');
          elements.forEach(x => {
            x.classList.remove('col-error');
          });
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";
@import "./src/assets/scss/components/table.scss";

.fts-table-edit-container {
  tr.selected-row > td:focus {
    &:after {
      content: '';
      inset: 2px;
      position: absolute;
      border-radius: 0.25rem;
      box-shadow: rgba(var(--fts-primary-rgb), 0.5) 0px 0px 0px 1px inset, rgba(var(--fts-primary-rgb), 0.5) 0px 0px 0px 2px;
      pointer-events: none;
      transition: box-shadow 0.3s ease-in-out;
    }
  }
  td {
    &.col-error {
      @include theme() {
        background-color: rgba($color: theme-get('fts-danger'), $alpha: .1);
      }
    }
    &.col-uploading {
      .form-element-edit {
        display: none;
      }
    }
    .upload-overlay {
      @include flex-center-middle;
      position: absolute;
      height: 100%;
      width: 100%;
      @include theme() {
        background-color: rgba($color: theme-get('fts-gray-200'), $alpha: .75);
      }
      pointer-events: none;
      z-index: 1;
    }
  }
  .fts-display-v2 {
    .display-signature-control {
      padding: .25rem;
      text-align: center;
      .display-signature-control-btn {
        display: inline-flex;
        margin-top: 2px;
        margin-bottom: 2px;
        padding: .375rem .75rem;
        border-radius: .25rem;
        font-size: .75rem;
        line-height: 1.5;
        color: var(--fts-form-label);
        cursor: pointer;
      }
    }
    &:hover {
      .display-signature-control {
        .display-signature-control-btn {
          background-color: var(--fts-gray-100);
        }
      }
    }
  }
}
</style>